import React, { useState } from "react";
import { AppBar, Toolbar, Typography, Box, IconButton, Avatar, Menu, MenuItem, useTheme } from "@mui/material";
import { AccountCircleOutlined as AccountCircleIcon, Logout as LogoutIcon } from "@mui/icons-material";
import { getClientAssets } from "../../services/client-assets";

interface PortalHeaderProps {
  clientData: {
    subdomain: string;
    clientName: string;
  };
  onLogout: () => void;
}

const PortalHeader: React.FC<PortalHeaderProps> = ({ clientData, onLogout }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [logoError, setLogoError] = useState(false);

  const { logoUrl, fallbackLogoUrl } = getClientAssets(clientData.subdomain);
  const isMenuOpen = Boolean(anchorEl);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    onLogout();
  };

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`,
        color: theme.palette.text.primary,
      }}
    >
      <Toolbar sx={{ minHeight: "64px", px: 2 }}>
        {/* Client Logo */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            "&:hover": {
              cursor: "pointer",
              opacity: 0.9,
              transition: "opacity 0.3s ease",
            },
          }}
        >
          <Box
            component="img"
            src={logoError ? fallbackLogoUrl : logoUrl}
            alt={`${clientData.clientName} Logo`}
            onError={() => setLogoError(true)}
            sx={{
              height: "48px",
              width: "auto",
              objectFit: "contain",
              maxWidth: "200px",
            }}
          />
          <Typography
            variant="h6"
            component="div"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
            }}
          >
            Privacy Portal
          </Typography>
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        {/* User Menu */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              display: { xs: "none", sm: "block" },
            }}
          >
            Welcome to {clientData.clientName}
          </Typography>
          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            aria-controls="primary-search-account-menu"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            color="inherit"
          >
            <AccountCircleIcon />
          </IconButton>
        </Box>

        {/* User Menu Dropdown */}
        <Menu
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          keepMounted
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          open={isMenuOpen}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleLogout}>
            <LogoutIcon sx={{ mr: 1 }} />
            Logout
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default PortalHeader;
